import { useCallback } from 'react';
import printJS from 'print-js';
import {
  PrintJob,
  PrintReceiptResponse,
} from '../api/types/vendor/carts-types';
import { useAlert } from '../contexs/alert-context';

/**
 * Custom hook that provides a print service for handling print jobs.
 *
 * It distinguishes between PDF print jobs and native print jobs:
 * - PDF print jobs are processed directly using printJS.
 * - Native print jobs are sent to the React Native WebView in a single message.
 *   The hook listens for a 'print-complete' response (with a unique id) via a message event,
 *   and resolves or rejects based on the response status.
 *
 * @returns An object containing:
 *   - `print`: A function that executes an array of `PrintJob` objects.
 *   - `processPrintJobs`: A function that processes printing from a `PrintReceiptResponse`, displaying an alert on failure.
 *   - `fetchAndExecutePrintJobs`: A function that retrieves and processes print jobs from a given promise.
 */
export const usePrintService = () => {
  const customAlert = useAlert();

  const print = useCallback(async (printJobs: PrintJob[]): Promise<void> => {
    // Split the print jobs into two categories:
    // - PDF print jobs (handled by printJS)
    // - Native print jobs (sent to the WebView)
    const pdfPrintJobs = printJobs.filter(
      job => job.printer_communication_type === 'pdf',
    );
    const nativePrintJobs = printJobs.filter(
      job => job.printer_communication_type !== 'pdf',
    );

    // Wrap the printing process in a promise that resolves when the native jobs complete,
    // or immediately if there are no native print jobs.
    return new Promise((resolve, reject) => {
      // Generate a unique ID for tracking native print jobs.
      const id = Date.now().toString();

      // Handler for receiving messages from the WebView.
      // Expects a 'print-complete' message with a matching id.
      const handleMessage = (event: any) => {
        const data = event.data;
        if (data?.type === 'print-complete' && data?.payload?.id === id) {
          document.removeEventListener('message', handleMessage);
          if (data.payload.status === 'success') {
            resolve();
          } else {
            reject(new Error(data.payload.message));
          }
        }
      };

      if (window.ReactNativeWebView) {
        // Listen for the 'print-complete' event on the document.
        document.addEventListener('message', handleMessage);
        // Send native print jobs to the WebView
        if (window.MESSAGE_PROCESSOR_VERSION === '1.0.0') {
          // Batch all print jobs into a single message
          window.ReactNativeWebView.postMessage(
            JSON.stringify({
              type: 'print',
              payload: { id, printJobs: nativePrintJobs },
            }),
          );
        } else {
          // Send each print job individually
          printJobs.forEach((printJob: PrintJob) => {
            window.ReactNativeWebView.postMessage(
              JSON.stringify({
                type: 'print',
                payload: printJob,
              }),
            );
          });

          resolve();
        }
      }

      // Process PDF print jobs directly.
      try {
        for (const printJob of pdfPrintJobs) {
          printJS({
            printable: printJob.pos_command,
            type: 'pdf',
            base64: true,
          });
        }
      } catch (error: unknown) {
        reject(error);
      }

      // If there are no native print jobs, resolve immediately.
      if (!nativePrintJobs.length || !window.ReactNativeWebView) {
        resolve();
      }

      // Set a timeout to reject the promise if no response is received within 15 seconds.
      setTimeout(() => {
        document.removeEventListener('message', handleMessage);
        reject(new Error('Print job timed out'));
      }, 15000);
    });
  }, []);

  /**
   * Processes print jobs from a `PrintReceiptResponse` object.
   * Displays an alert if an error occurs.
   *
   * @param {PrintReceiptResponse} data - The response containing print job details.
   * @returns {Promise<void>}
   */
  const processPrintJobs = async (
    data: PrintReceiptResponse,
  ): Promise<void> => {
    try {
      if (data.detail) {
        throw new Error(data.detail);
      }
      await print(data.print_jobs);
    } catch (error: any) {
      customAlert.show(error.message);
    }
  };

  /**
   * Fetches and processes print jobs from a given promise.
   *
   * - Awaits the resolution of the provided `printJobPromise` to retrieve print jobs.
   * - Passes the retrieved jobs to `processPrintJobs` for execution.
   * - If an error occurs during the fetching or processing of print jobs, it displays an alert but does not throw.
   *
   * @param {Promise<PrintReceiptResponse>} printJobPromise - A promise that resolves to the print jobs to be processed.
   * @returns {Promise<void>} A promise that resolves when printing is completed.
   */
  const fetchAndExecutePrintJobs = async (
    printJobPromise: Promise<PrintReceiptResponse>,
  ): Promise<void> => {
    try {
      const data = await printJobPromise;
      await processPrintJobs(data);
    } catch (error) {
      customAlert.show('Print failed'); // Only shows an alert, does not throw.
    }
  };

  return { print, processPrintJobs, fetchAndExecutePrintJobs };
};
